import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiService from '../../../services/api';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { subscriptionService } from '../../../services/subscriptionService';
import { creditService } from '../../../services/creditService';
import { useCredits } from '../../../hooks/useCredits';
import { CREDIT_COSTS, CREDIT_THRESHOLDS } from '../../../config/creditCosts';
import LowCreditModal from '../../../components/LowCreditModal';
import CreditDeductionConfirmModal from '../../../components/CreditDeductionConfirmModal';

interface FAQProps {
  onTooltipShow: (title: string, content: string) => void;
  onBack: () => void;
  darkMode?: boolean;
}

interface PageNode {
  id: string;
  name: string;
  selected: boolean;
  type?: 'page' | 'category' | 'service' | 'product' | 'info';
  url?: string;
  isOnboarding?: boolean;
  badge?: string;
  children?: PageNode[];
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

interface GeneratedFAQ {
  id: number;
  pageName: string;
  faqs: FAQItem[];
  uploaded: boolean;
  status: string;
  timestamp: string;
}

export default function FAQ({ onTooltipShow, onBack, darkMode }: FAQProps) {
  const { invalidateCredits } = useCredits();
  const [activeStep, setActiveStep] = useState<'selection' | 'preview'>('selection');

  // Low Credit Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCredits, setModalCredits] = useState(0);
  const [modalRequired, setModalRequired] = useState(0);
  const [modalIsBlocker, setModalIsBlocker] = useState(false);

  // Credit Deduction Confirm Modal States & Quota
  const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number } | null>(null);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [creditConfirmAmount, setCreditConfirmAmount] = useState(0);
  const [creditConfirmBalance, setCreditConfirmBalance] = useState(0);
  const [creditConfirmDescription, setCreditConfirmDescription] = useState('');

  const checkCreditsForAction = async (required: number): Promise<{ allowed: boolean; current: number }> => {
    try {
      const balanceData = await creditService.getBalance();
      const current = balanceData?.total_credits_balance ?? 0;
      
      if (current < required) {
        setModalCredits(current);
        setModalRequired(required);
        setModalIsBlocker(true);
        setModalOpen(true);
        return { allowed: false, current };
      }
      return { allowed: true, current };
    } catch (err) {
      console.error("Failed to check credits:", err);
    }
    return { allowed: true, current: 0 };
  };

  const checkLowCreditWarning = async (current: number, quotaRemaining?: number) => {
    const remaining = quotaRemaining !== undefined ? quotaRemaining : (quota?.remaining ?? 0);
    if (remaining > 0) {
      return;
    }
    if (current < CREDIT_THRESHOLDS.LOW_CREDIT_WARNING) {
      setModalCredits(current);
      setModalIsBlocker(false);
      setModalOpen(true);
    }
  };
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmittingGeneration, setIsSubmittingGeneration] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatingPageName, setGeneratingPageName] = useState('');
  const [generatingIndex, setGeneratingIndex] = useState(0);
  const [statusTogglingId, setStatusTogglingId] = useState<number | null>(null);
  const [toastError, setToastError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<string[]>(['root']);
  const [editingPageId, setEditingPageId] = useState<number | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [expandedSavedPageIds, setExpandedSavedPageIds] = useState<number[]>([]);
  const [expandedPreviewPageIds, setExpandedPreviewPageIds] = useState<number[]>([]);

  /* New State for Dynamic Loading */
  const [loadingStructure, setLoadingStructure] = useState(true);
  const [requestedCount, setRequestedCount] = useState(0);

  // Initialize with empty array, will be populated by API
  const [pageTree, setPageTree] = useState<PageNode[]>([]);
  const [savedFAQs, setSavedFAQs] = useState<GeneratedFAQ[]>([]);
  const [activeTab, setActiveTab] = useState<'generate' | 'saved'>('generate');
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `FAQ Generator / ${activeTab === 'generate' ? 'Generate' : 'Saved FAQs'}`,
    routePath: `/dashboard#faq/${activeTab}`,
  });

  const userId = localStorage.getItem('user_id');

  // Fetch Page Structure from Backend
  useEffect(() => {
    const fetchStructure = async () => {
      if (!userId) {
        setLoadingStructure(false);
        return;
      }

      try {
        const json: any = await apiService.getFAQStructure(userId);
        const mapNode = (node: any): PageNode => ({
          id: node.id,
          name: node.label,
          type: node.type,
          url: node.url,
          selected: false,
          isOnboarding: !!(node.isOnboarding || node.is_onboarding),
          badge: node.badge,
          children: node.children ? node.children.map(mapNode) : undefined
        });
        const structure = (json.data?.structure || []).map(mapNode);
        setPageTree(structure);
        // Expand the single Website Pages folder by default
        setExpandedNodes(structure.map((n: PageNode) => n.id));
      } catch (e) {
        console.error("Failed to fetch FAQ structure", e);
      } finally {
        setLoadingStructure(false);
      }
    };

    fetchStructure();
  }, [userId]);

  // Fetch Saved FAQs
  const fetchSavedFAQs = async () => {
    if (!userId) return;
    try {
      const json: any = await apiService.listFAQs(userId);
      const faqsList = (json.data?.faqs || []).map((f: any) => ({
        ...f,
        faqs: (f.faqs || []).map((faq: any, i: number) => ({ ...faq, id: faq.id || (i + 1) })),
        uploaded: f.status === 'published',
      }));
      setSavedFAQs(faqsList);
      if (faqsList.length > 0) {
        setExpandedSavedPageIds(prev => prev.length === 0 ? [faqsList[0].id] : prev);
      }
    } catch (e) {
      console.error("Failed to fetch saved FAQs", e);
    }
  };

  const fetchFAQQuota = async () => {
    if (!userId) return null;
    try {
      const json: any = await apiService.getFAQQuota(userId);
      const q = json.data?.quota || json.data;
      setQuota(q);
      return q;
    } catch (e) {
      console.error("Failed to fetch FAQ quota", e);
      return null;
    }
  };

  useEffect(() => {
    fetchSavedFAQs();
    fetchFAQQuota();
  }, [userId]);

  // Listen to step changes to reset expanded nodes (optional UI polish)
  const [generatedFAQs, setGeneratedFAQs] = useState<GeneratedFAQ[]>([]);

  const toggleSavedPageExpand = (pageId: number) => {
    setExpandedSavedPageIds(prev =>
      prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]
    );
  };

  const togglePreviewPageExpand = (pageId: number) => {
    setExpandedPreviewPageIds(prev =>
      prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]
    );
  };

  const expandAllSaved = () => {
    setExpandedSavedPageIds(savedFAQs.map(f => f.id));
  };

  const collapseAllSaved = () => {
    setExpandedSavedPageIds([]);
  };


  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev =>
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handlePageToggle = (targetId: string) => {
    // Helper to find node type
    const findNode = (nodes: PageNode[]): PageNode | null => {
      for (const node of nodes) {
        if (node.id === targetId) return node;
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    const targetNode = findNode(pageTree);
    if (!targetNode) return;

    // Ignore clicks on Categories or Info
    if (targetNode.type === 'category' || targetNode.type === 'info') {
      toggleNode(targetId); // Just toggle expansion
      return;
    }

    setPageTree(prevTree => {
      // Recursive update function
      const updateTree = (nodes: PageNode[]): PageNode[] => {
        return nodes.map(node => {
          let updatedChildren = node.children ? updateTree(node.children) : undefined;
          let isSelected = node.selected;

          // Toggle Target
          if (node.id === targetId) {
            isSelected = !node.selected;
          }

          return {
            ...node,
            selected: isSelected,
            children: updatedChildren
          };
        });
      };

      return updateTree(prevTree);
    });
  };

  const getSelectedPages = (nodes: PageNode[] = pageTree): string[] => {
    let selected: string[] = [];
    nodes.forEach(node => {
      if (node.selected) {
        selected.push(node.name);
      }
      if (node.children) {
        selected = [...selected, ...getSelectedPages(node.children)];
      }
    });
    return selected;
  };

  // Helper to collect selected pages with URLs
  const getSelectedNodes = (nodes: PageNode[]): PageNode[] => {
    let selected: PageNode[] = [];
    nodes.forEach(node => {
      if (node.selected) {
        selected.push(node);
      }
      if (node.children) {
        selected = [...selected, ...getSelectedNodes(node.children)];
      }
    });
    return selected;
  };

  const handleGenerateFAQ = async () => {
    if (isGenerating || isSubmittingGeneration) return;
    const selectedNodes = getSelectedNodes(pageTree);
    if (selectedNodes.length === 0) return;

    setIsSubmittingGeneration(true);
    try {
      // Re-fetch fresh quota to calculate free/paid split accurately
      let latestQuota = quota;
      try {
        if (userId) {
          const json: any = await apiService.getFAQQuota(userId);
          const q = json.data?.quota || json.data;
          if (q) {
            setQuota(q);
            latestQuota = q;
          }
        }
      } catch (e) {
        console.error("Failed to refresh FAQ quota before generation check", e);
      }

      const freeRemaining = Math.max(0, latestQuota?.remaining ?? 0);
      let availableFree = freeRemaining;
      let paidCount = 0;
      let freeCount = 0;

      for (const _node of selectedNodes) {
        if (availableFree > 0) {
          availableFree--;
          freeCount++;
        } else {
          paidCount++;
        }
      }

      if (paidCount === 0) {
        await runGeneration(selectedNodes);
        return;
      }

      // Only overage beyond free quota is charged
      const required = paidCount * CREDIT_COSTS.SEO_FAQ;
      const checkRes = await checkCreditsForAction(required);
      if (!checkRes.allowed) return;

      setCreditConfirmAmount(required);
      setCreditConfirmBalance(checkRes.current);

      if (freeCount > 0) {
        setCreditConfirmDescription(
          `${freeCount} FAQ set${freeCount > 1 ? 's' : ''} in your batch ${freeCount > 1 ? 'are' : 'is'} covered by your free monthly quota. The remaining ${paidCount} FAQ set${paidCount > 1 ? 's' : ''} will cost ${required.toLocaleString()} credits (${CREDIT_COSTS.SEO_FAQ} credits each), charged upfront.`
        );
      } else {
        const limit = latestQuota?.limit || 0;
        if (limit > 0) {
          setCreditConfirmDescription(
            `You've used your free monthly FAQ quota. Generating ${selectedNodes.length} FAQ set${selectedNodes.length > 1 ? 's' : ''} will cost ${required.toLocaleString()} credits (${CREDIT_COSTS.SEO_FAQ} credits each), charged upfront.`
          );
        } else {
          setCreditConfirmDescription(
            `Generating ${selectedNodes.length} FAQ set${selectedNodes.length > 1 ? 's' : ''} will cost ${required.toLocaleString()} credits (${CREDIT_COSTS.SEO_FAQ} credits each), charged upfront.`
          );
        }
      }

      setShowCreditConfirm(true);
    } finally {
      setIsSubmittingGeneration(false);
    }
  };

  const handleConfirmFAQGeneration = async () => {
    const selectedNodes = getSelectedNodes(pageTree);
    setShowCreditConfirm(false);
    await runGeneration(selectedNodes);
  };

  const runGeneration = async (selectedNodes: PageNode[]) => {
    setIsGenerating(true);
    setRequestedCount(selectedNodes.length);
    const results: GeneratedFAQ[] = [];
    const errorMessages: string[] = [];

    for (let i = 0; i < selectedNodes.length; i++) {
      const node = selectedNodes[i];
      setGeneratingIndex(i + 1);
      setGeneratingPageName(node.name);
      setGenerationProgress(Math.round((i / selectedNodes.length) * 90) + 5);

      try {
        const json: any = await apiService.generateFAQ(userId!, {
          id: node.id,
          name: node.name,
          url: node.url,
        });
        const r = json.data?.result;
        if (r && json.status === 'success') {
          results.push({
            id: r.id,
            pageName: r.pageName,
            faqs: (r.faqs || []).map((faq: any, i: number) => ({ ...faq, id: faq.id || (i + 1) })),
            uploaded: false,
            status: 'draft',
            timestamp: r.timestamp || new Date().toLocaleString(),
          });
        } else {
          const msg = json?.message || `Failed to generate FAQs for '${node.name}'`;
          errorMessages.push(msg);
        }
      } catch (e: any) {
        console.error(`FAQ generation failed for ${node.name}`, e);
        const rawErr = e?.response?.data?.detail || e?.message || e?.detail || 'Generation failed';
        let cleanErr = typeof rawErr === 'string' ? rawErr : JSON.stringify(rawErr);
        if (cleanErr.includes('CREDITS_FROZEN_OR_EXPIRED')) {
          cleanErr = '400: CREDITS_FROZEN_OR_EXPIRED. Your credits/quota are frozen due to an expired subscription.';
        }
        errorMessages.push(`Failed to generate FAQ for '${node.name}'. ${cleanErr}`);
      }
    }

    setGenerationProgress(100);

    // Refresh Quota & shared credits balance
    let latestQuotaRemaining: number | undefined = undefined;
    try {
      const q = await fetchFAQQuota();
      if (q) {
        latestQuotaRemaining = q.remaining ?? 0;
      }
    } catch (_) {}
    invalidateCredits();

    // Check low credit warning with latest credits only if no free quota remaining
    try {
      const balanceData = await creditService.getBalance();
      const current = balanceData?.total_credits_balance ?? 0;
      await checkLowCreditWarning(current, latestQuotaRemaining);
    } catch (err) {
      console.error("Failed to check warning:", err);
    }

    if (errorMessages.length > 0) {
      errorMessages.forEach(err => {
        toast.error('FAQ Generation Failed', {
          description: err,
          duration: 8000,
        });
      });
      setToastError(errorMessages[0]);
      setTimeout(() => setToastError(''), 6000);
    }

    if (results.length > 0) {
      if (errorMessages.length === 0) {
        toast.success('FAQ Generation Complete', {
          description: `Successfully generated FAQ set${results.length > 1 ? 's' : ''} for ${results.length} page${results.length > 1 ? 's' : ''}.`,
          duration: 5000,
        });
      }
      setGeneratedFAQs(results);
      setExpandedPreviewPageIds(results.map(r => r.id));
      setActiveStep('preview');
      setPageTree(prev => {
        const reset = (nodes: PageNode[]): PageNode[] => nodes.map(n => ({
          ...n,
          selected: false,
          children: n.children ? reset(n.children) : undefined,
        }));
        return reset(prev);
      });
      fetchSavedFAQs();
    }

    setIsGenerating(false);
    setGenerationProgress(0);
    setGeneratingPageName('');
    setGeneratingIndex(0);
  };

  const handleEditFAQ = (pageId: number, faqId: number, field: 'question' | 'answer', value: string) => {
    setGeneratedFAQs(generatedFAQs.map(page => {
      if (page.id === pageId) {
        return {
          ...page,
          faqs: page.faqs.map(faq => {
            if (faq.id === faqId) {
              return { ...faq, [field]: value };
            }
            return faq;
          })
        };
      }
      return page;
    }));
  };

  const handleToggleUpload = async (pageId: number) => {
    const page = generatedFAQs.find(p => p.id === pageId);
    if (!page || statusTogglingId !== null) return;
    const newStatus = page.uploaded ? 'draft' : 'published';
    setStatusTogglingId(pageId);
    try {
      await apiService.updateFAQStatus(pageId, newStatus, userId!);
      setGeneratedFAQs(prev =>
        prev.map(p => p.id === pageId ? { ...p, uploaded: !p.uploaded, status: newStatus } : p)
      );
    } catch (e) {
      setToastError('Failed to update status. Please try again.');
      setTimeout(() => setToastError(''), 4000);
    } finally {
      setStatusTogglingId(null);
    }
  };

  const handleToggleSavedStatus = async (pageId: number) => {
    const page = savedFAQs.find(p => p.id === pageId);
    if (!page || statusTogglingId !== null) return;
    const newStatus = page.uploaded ? 'draft' : 'published';
    setStatusTogglingId(pageId);
    try {
      await apiService.updateFAQStatus(pageId, newStatus, userId!);
      setSavedFAQs(prev =>
        prev.map(p => p.id === pageId ? { ...p, uploaded: !p.uploaded, status: newStatus } : p)
      );
    } catch (e) {
      setToastError('Failed to update status. Please try again.');
      setTimeout(() => setToastError(''), 4000);
    } finally {
      setStatusTogglingId(null);
    }
  };

  const handleDownload = (page: GeneratedFAQ) => {
    const content = page.faqs.map(faq =>
      `Q: ${faq.question}\nA: ${faq.answer}\n`
    ).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${page.pageName.replace(/\s+/g, '-')}-FAQ.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleStartEditPage = (page: GeneratedFAQ) => {
    if (!expandedSavedPageIds.includes(page.id)) {
      setExpandedSavedPageIds(prev => [...prev, page.id]);
    }
    if (!expandedPreviewPageIds.includes(page.id)) {
      setExpandedPreviewPageIds(prev => [...prev, page.id]);
    }
    if (editingPageId === page.id) {
      setEditingPageId(null);
    } else {
      setEditingPageId(page.id);
    }
  };

  const handleSavePageEdit = async (page: GeneratedFAQ) => {
    if (!userId) return;
    setIsSavingEdit(true);
    try {
      await apiService.updateFAQContent(page.id, page.faqs, userId);
      setEditingPageId(null);
    } catch (e) {
      console.error("Failed to save FAQ edits", e);
      setToastError('Failed to save edits. Please try again.');
      setTimeout(() => setToastError(''), 4000);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleEditSavedFAQ = (pageId: number, faqId: number, field: 'question' | 'answer', value: string) => {
    setSavedFAQs(prev => prev.map(page => {
      if (page.id !== pageId) return page;
      return {
        ...page,
        faqs: page.faqs.map(faq => {
          if (faq.id !== faqId) return faq;
          return { ...faq, [field]: value };
        })
      };
    }));
  };

  const renderPageTree = (nodes: PageNode[], level: number = 0) => {
    return nodes.map(node => {
      const isCategory = node.type === 'category' || node.type === 'info';
      return (
        <div key={node.id} style={{ marginLeft: `${level * 20}px` }}>
          <div
            className={`flex items-center space-x-2 p-3 rounded-lg transition-all cursor-pointer ${node.selected
              ? 'bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-600'
              : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`}
            onClick={() => handlePageToggle(node.id)}
          >
            {node.children && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.id);
                }}
                className={`w-5 h-5 flex items-center justify-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                <i className={`ri-arrow-${expandedNodes.includes(node.id) ? 'down' : 'right'}-s-line`}></i>
              </button>
            )}
            {!node.children && <div className="w-5"></div>}

            {/* Only render checkbox for selectable page/service/product nodes */}
            {!isCategory && (
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${node.selected
                ? 'bg-blue-600 border-blue-600'
                : darkMode ? 'border-gray-600' : 'border-gray-300'
                }`}>
                {node.selected && <i className="ri-check-line text-white text-xs"></i>}
              </div>
            )}

            <i className={`${isCategory ? 'ri-folder-line text-blue-500' : 'ri-file-text-line'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}></i>
            <span className={`flex-1 ${isCategory ? 'font-semibold text-sm' : 'text-sm'} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {node.name}
            </span>

            {/* Onboarding Badge */}
            {node.isOnboarding && (
              <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-full font-medium ml-2 inline-flex items-center gap-1 flex-shrink-0">
                <i className="ri-checkbox-circle-line text-blue-500 text-[11px]"></i>
                Onboarding
              </span>
            )}

            {/* Check if this page already has generated FAQs */}
            {node.url && savedFAQs.some(f => f.pageName === node.name || f.pageName === node.url) && (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full font-medium ml-2">
                Done
              </span>
            )}
          </div>

          {
            node.children && expandedNodes.includes(node.id) && (
              <div className="mt-1">
                {renderPageTree(node.children, level + 1)}
              </div>
            )
          }
        </div >
      );
    });
  };

  const selectedCount = getSelectedPages().length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={onBack}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back
        </button>
        <div className="flex items-center gap-3">
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            FAQ Generator
          </h2>
          <button
            className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group relative"
            onClick={(e) => {
              e.stopPropagation();
              const t = document.getElementById('faq-seo-tooltip');
              if (t) {
                t.classList.toggle('opacity-0');
                t.classList.toggle('opacity-100');
                setTimeout(() => {
                  t.classList.add('opacity-0');
                  t.classList.remove('opacity-100');
                }, 4000);
              }
            }}
          >
            <i className="ri-information-line text-lg"></i>
            <div
              id="faq-seo-tooltip"
              className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
            >
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-line text-blue-600 dark:text-blue-400"></i>
                FAQ Generator
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Select pages from your sitemap to generate relevant FAQs based on page content and user intent.
              </p>
            </div>
          </button>
        </div>
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Select pages to automatically generate SEO-optimized FAQs
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('generate')}
          className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'generate'
            ? 'border-blue-600 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
        >
          Generator
        </button>
        <button
          onClick={() => setActiveTab('saved')}
          className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'saved'
            ? 'border-blue-600 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
        >
          Saved FAQs ({savedFAQs.length})
        </button>
      </div>

      {/* View: Saved FAQs */}
      {activeTab === 'saved' && (
        <div className="space-y-4">
          {savedFAQs.length === 0 ? (
            <div className={`text-center py-12 rounded-xl border border-dashed ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
              <i className="ri-file-list-3-line text-4xl mb-3 block opacity-50"></i>
              <p>No FAQs generated yet.</p>
              <button
                onClick={() => setActiveTab('generate')}
                className="mt-4 text-blue-600 hover:underline text-sm font-medium"
              >
                Go to Generator
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header Controls */}
              <div className="flex items-center justify-between px-1">
                <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {savedFAQs.length} Page{savedFAQs.length > 1 ? 's' : ''} Saved
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={expandAllSaved}
                    className={`text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <i className="ri-expand-up-down-line mr-1"></i>
                    Expand All
                  </button>
                  <span className="text-gray-300 dark:text-gray-700">|</span>
                  <button
                    type="button"
                    onClick={collapseAllSaved}
                    className={`text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <i className="ri-contract-up-down-line mr-1"></i>
                    Collapse All
                  </button>
                </div>
              </div>

              {/* Cards Grid */}
              <div className="grid grid-cols-1 gap-4">
                {savedFAQs.map((page) => {
                  const isExpanded = expandedSavedPageIds.includes(page.id) || editingPageId === page.id;
                  return (
                    <div key={page.id} className={`rounded-xl border transition-all overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                      {/* Accordion Header */}
                      <div
                        onClick={() => toggleSavedPageExpand(page.id)}
                        className={`p-4 flex items-center justify-between cursor-pointer select-none transition-colors ${isExpanded ? (darkMode ? 'border-b border-gray-700 bg-gray-700/20' : 'border-b border-gray-100 bg-gray-50/80') : (darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50')}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <button
                            type="button"
                            className={`w-6 h-6 flex items-center justify-center rounded transition-transform duration-200 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-lg`}></i>
                          </button>
                          <i className="ri-file-text-line text-blue-500 text-lg flex-shrink-0"></i>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'} truncate`}>
                                {page.pageName}
                              </h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                {page.faqs.length} FAQs
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${page.uploaded
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                                {page.uploaded ? 'Published' : 'Draft'}
                              </span>
                            </div>
                            {page.timestamp && (
                              <p className="text-xs text-gray-400 mt-0.5">{page.timestamp}</p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons (clicks prevented from toggling accordion) */}
                        <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => editingPageId === page.id ? handleSavePageEdit(page) : handleStartEditPage(page)}
                            disabled={isSavingEdit && editingPageId === page.id}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${editingPageId === page.id
                              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                              : darkMode ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            {isSavingEdit && editingPageId === page.id && <Loader2 className="w-3 h-3 animate-spin" />}
                            {editingPageId === page.id ? 'Save Changes' : 'EDIT'}
                          </button>
                          <button
                            onClick={() => handleDownload(page)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${darkMode ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            title="Download"
                          >
                            <i className="ri-download-line font-normal"></i>
                            Download
                          </button>
                          <button
                            onClick={() => handleToggleSavedStatus(page.id)}
                            disabled={statusTogglingId === page.id}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${page.uploaded
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200'
                              : 'bg-blue-600 text-white hover:bg-blue-700'} disabled:opacity-50`}
                          >
                            {statusTogglingId === page.id && <Loader2 className="w-3 h-3 animate-spin" />}
                            {page.uploaded ? 'Published' : 'Publish'}
                          </button>
                        </div>
                      </div>

                      {/* Accordion Body: FAQs List */}
                      {isExpanded && (
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {page.faqs.map((faq, idx) => (
                            <div key={faq.id || idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                              {editingPageId === page.id ? (
                                <div className="space-y-3">
                                  <div>
                                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                                      Question {idx + 1}
                                    </label>
                                    <input
                                      value={faq.question}
                                      onChange={(e) => handleEditSavedFAQ(page.id, faq.id, 'question', e.target.value)}
                                      className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                      placeholder="Enter question"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                                      Answer
                                    </label>
                                    <textarea
                                      value={faq.answer}
                                      onChange={(e) => handleEditSavedFAQ(page.id, faq.id, 'answer', e.target.value)}
                                      className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                      rows={3}
                                      placeholder="Enter answer"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    Q: {faq.question}
                                  </h4>
                                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    A: {faq.answer}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                          {editingPageId === page.id && (
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-2 border-t border-gray-100 dark:border-gray-700">
                              <button
                                onClick={() => setEditingPageId(null)}
                                className="px-4 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleSavePageEdit(page)}
                                disabled={isSavingEdit}
                                className="px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                              >
                                {isSavingEdit && <Loader2 className="w-3 h-3 animate-spin" />}
                                Save All Changes
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}



      {/* Step 1: Page Selection */}
      {activeTab === 'generate' && activeStep === 'selection' && (
        <div className={`rounded-xl p-6 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Select Pages
            </h3>
            <div className="flex items-center gap-4">
              {quota && (
                quota.limit > 0 ? (
                  quota.remaining > 0 ? (
                    <span className="text-sm font-medium text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300 px-3 py-1 rounded-full border border-emerald-200 dark:border-emerald-700 flex items-center gap-1.5">
                      <i className="ri-gift-line"></i>
                      {quota.used} of {quota.limit} free FAQs generated ({quota.remaining} remaining)
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-700 flex items-center gap-1.5">
                      <i className="ri-error-warning-line"></i>
                      {quota.limit} free FAQs quota exhausted — Generating FAQs uses {CREDIT_COSTS.SEO_FAQ} credits each.
                    </span>
                  )
                ) : (
                  <span className="text-sm font-medium text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-300 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-700 flex items-center gap-1.5">
                    <i className="ri-information-line"></i>
                    Generating FAQs uses {CREDIT_COSTS.SEO_FAQ} credits each.
                  </span>
                )
              )}
              <span className={`text-sm font-bold ${selectedCount > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                {selectedCount} Selected
              </span>
            </div>
          </div>

          <div className="space-y-2 mb-8 max-h-[60vh] overflow-y-auto">
            {loadingStructure ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : (
              renderPageTree(pageTree)
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleGenerateFAQ}
              disabled={selectedCount === 0 || isGenerating || isSubmittingGeneration}
              className={`px-8 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                selectedCount > 0 && !isGenerating && !isSubmittingGeneration
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg cursor-pointer'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="animate-spin w-5 h-5" />
                  Generating FAQs...
                </>
              ) : isSubmittingGeneration ? (
                <>
                  <Loader2 className="animate-spin w-5 h-5" />
                  Checking Credits...
                </>
              ) : (
                <>
                  <i className="ri-magic-line"></i>
                  Generate FAQs
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Generation Progress Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className={`p-8 rounded-xl shadow-2xl max-w-md w-full text-center border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-white'}`}>
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Generating FAQs...
            </h3>
            <p className={`mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {generatingPageName ? `Generating FAQ for "${generatingPageName}"` : 'Analyzing page content and creating optimized questions.'}
            </p>
            {requestedCount > 1 && (
              <p className={`text-sm mb-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {generatingIndex} of {requestedCount}
              </p>
            )}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-2 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${generationProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {generationProgress}% Complete
            </p>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {toastError && (
        <div className="fixed bottom-6 right-6 z-50 bg-red-600 text-white px-5 py-3 rounded-lg shadow-lg text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {toastError}
        </div>
      )}

      {/* Step 2: Preview & Edit */}
      {activeTab === 'generate' && activeStep === 'preview' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setActiveStep('selection')}
                className={`text-sm flex items-center gap-1 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <i className="ri-arrow-left-line"></i> Back to Selection
              </button>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
                <i className="ri-checkbox-circle-fill text-blue-600"></i>
                <span className={`text-sm font-medium ${darkMode ? 'text-blue-100' : 'text-blue-700'}`}>
                  Generated FAQs ({generatedFAQs.length}/{requestedCount})
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button className={`px-4 py-2 rounded-lg text-sm font-medium border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                Export All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {generatedFAQs.map((page) => {
              const isExpanded = expandedPreviewPageIds.includes(page.id) || editingPageId === page.id;
              return (
                <div key={page.id} className={`rounded-xl border transition-all overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                  {/* Accordion Header */}
                  <div
                    onClick={() => togglePreviewPageExpand(page.id)}
                    className={`p-4 flex items-center justify-between cursor-pointer select-none transition-colors ${isExpanded ? (darkMode ? 'border-b border-gray-700 bg-gray-700/20' : 'border-b border-gray-100 bg-gray-50/80') : (darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50')}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        className={`w-6 h-6 flex items-center justify-center rounded transition-transform duration-200 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-lg`}></i>
                      </button>
                      <i className="ri-file-text-line text-blue-500 text-lg flex-shrink-0"></i>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'} truncate`}>
                            {page.pageName}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            {page.faqs.length} FAQs
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${page.uploaded
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                            {page.uploaded ? 'Published' : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => editingPageId === page.id ? handleSavePageEdit(page) : handleStartEditPage(page)}
                        disabled={isSavingEdit && editingPageId === page.id}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${editingPageId === page.id
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                          : darkMode ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {isSavingEdit && editingPageId === page.id && <Loader2 className="w-3 h-3 animate-spin" />}
                        {editingPageId === page.id ? 'Save Changes' : 'EDIT'}
                      </button>
                      <button
                        onClick={() => handleDownload(page)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${darkMode ? 'bg-gray-700 text-gray-100 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        title="Download"
                      >
                        <i className="ri-download-line font-normal"></i>
                        Download
                      </button>
                      <button
                        onClick={() => handleToggleUpload(page.id)}
                        disabled={statusTogglingId === page.id}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${page.uploaded
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200'
                          : 'bg-blue-600 text-white hover:bg-blue-700'} disabled:opacity-50`}
                      >
                        {statusTogglingId === page.id && <Loader2 className="w-3 h-3 animate-spin" />}
                        {page.uploaded ? 'Published' : 'Publish'}
                      </button>
                    </div>
                  </div>

                  {/* FAQs List */}
                  {isExpanded && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {page.faqs.map((faq, idx) => (
                        <div key={faq.id || idx} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          {editingPageId === page.id ? (
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                                  Question {idx + 1}
                                </label>
                                <input
                                  value={faq.question}
                                  onChange={(e) => handleEditFAQ(page.id, faq.id, 'question', e.target.value)}
                                  className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-medium text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  placeholder="Enter question"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-1">
                                  Answer
                                </label>
                                <textarea
                                  value={faq.answer}
                                  onChange={(e) => handleEditFAQ(page.id, faq.id, 'answer', e.target.value)}
                                  className="w-full p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                  rows={3}
                                  placeholder="Enter answer"
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                Q: {faq.question}
                              </h4>
                              <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                A: {faq.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                      {editingPageId === page.id && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-2 border-t border-gray-100 dark:border-gray-700">
                          <button
                            onClick={() => setEditingPageId(null)}
                            className="px-4 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSavePageEdit(page)}
                            disabled={isSavingEdit}
                            className="px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                          >
                            {isSavingEdit && <Loader2 className="w-3 h-3 animate-spin" />}
                            Save All Changes
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <LowCreditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentCredits={modalCredits}
        requiredCredits={modalRequired}
        isBlocker={modalIsBlocker}
      />
      <CreditDeductionConfirmModal
        isOpen={showCreditConfirm}
        onClose={() => setShowCreditConfirm(false)}
        onConfirm={handleConfirmFAQGeneration}
        currentCredits={creditConfirmBalance}
        deductCredits={creditConfirmAmount}
        isProcessing={false}
        description={creditConfirmDescription}
      />
    </div>
  );
}
